<template>
  <v-card
    :data-session-uuid="sessionObject.uuid"
    outlined
    rounded="lg"
    elevation="0"
    min-width="300"
    width="300"
    class="
      session-card border-left
      fit-content
    "
    :style="`height: 100%;--_border-left-color: ${sessionColor}; --_border-left-width: 8px;`"
  >
    <v-card-title
      class="
        pt-1
        body-2 font-weight-bold
      "
    >
      <v-btn
        text
        small
        class="
          px-0 flex justify-space-between
          body-2 font-weight-bold
        "
        @click="$emit('edit-session')"
      >
        <span class="text-truncate">{{ session.name }}</span>
        <v-icon medium color="grey darken-1" v-if="sessionAssignedHorsesCount > 0">mdi-horse-variant</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-subtitle
      class="
        d-flex justify-start align-center gap-1
        pb-1
        text-center caption font-weight-bold
      "
    >
      {{ sessionTimeLabel }}
      <v-skeleton-loader
        :loading="isInstructorsLoading"
        min-width="100"
        type="text"
      >
        <section>
          <span v-if="showSingleInstructor" class="d-flex align-center gap-1">
            . {{ sessionInstructors[0].name }}
            <v-icon
              v-if="instructorAssignedHorsesCount > 0"
              small
            >
              mdi-horse-variant
            </v-icon>
          </span>
          <v-tooltip
            v-else-if="showMultipleInstructors"
            bottom
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                small
                color="grey lighten-4"
                class="font-weight-bold caption"
                v-bind="attrs"
                v-on="on"
              >
                {{ sessionInstructors.length }} moniteur(s)
              </v-chip>
            </template>
            <template class="d-flex flex-column">
              <p
                v-for="(instructor, instructorIndex) in sessionInstructors"
                :key="instructorIndex"
                class="
                  d-flex justify-space-between align-center
                  my-0
                "
              >
                {{ instructor.name }}
                <v-icon
                  v-if="instructor.horse"
                  x-small
                  color="white"
                  class="ml-2"
                >
                  mdi-horse-variant
                </v-icon>
              </p>
            </template>
          </v-tooltip>
        </section>
      </v-skeleton-loader>
    </v-card-subtitle>

    <v-card-text
      class="
        gap-2
        d-flex flex-column
        pb-2 px-1
      "
    >
      <section class="d-flex justify-space-between">
        <v-chip
          small
          color="grey lighten-4"
          class="
            no-hover
            font-weight-bold caption
          "
        >
          {{ sortedParticipants.length }}/{{ session.max_participant }} cavaliers
        </v-chip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              small
              color="grey lighten-4"
              class="font-weight-bold caption"
              v-bind="attrs"
              v-on="on"
            >
              {{ participantAssignedHorsesCount }}/{{ sessionHorsesCount }} chevaux
            </v-chip>
          </template>

          <template>
            <p class="my-0">{{ participantAssignedHorsesCount !== 1 ? `${participantAssignedHorsesCount} chevaux affectés aux cavaliers` : `${participantAssignedHorsesCount} cheval affecté aux cavaliers` }}</p>
            <p class="my-0">{{ sessionAssignedHorsesCount !== 1 ? `${sessionAssignedHorsesCount} chevaux réservés pour la reprise/session` : `${sessionAssignedHorsesCount} cheval réservé pour la reprise/session` }}</p>
            <p class="my-0">{{ instructorAssignedHorsesCount !== 1 ? `${instructorAssignedHorsesCount} chevaux utilisés par les moniteurs` : `${instructorAssignedHorsesCount} cheval utilisé par les moniteurs` }}</p>
          </template>
        </v-tooltip>
      </section>

      <v-list
        outlined
        dense
        :max-height="305"
        class="
          redScroll
          overflow-y-auto
          pa-0 rounded-lg
        "
      >
        <template v-for="(participant, participantIndex) in sortedParticipants">
          <v-list-item
            :key="participant.uuid"
            :class="{'border-bottom' : sortedParticipants.length > session.max_participant && session.max_participant === participantIndex + 1 }"
            :style="`min-height: 24px; --_border-bottom-color: var(--v-third-lighten1); --_border-bottom-width: 4px;`"
          >
            <v-list-item-title
              class="
                my-0
                font-weight-regular caption
              "
            >{{ participantName(participant) }}</v-list-item-title>

            <v-list-item-icon
              v-show="!!participant.horse"
              class="
                align-self-center
                ma-0
              "
              style="height: 18px;"
            >
              <v-icon size="20">mdi-horse-variant</v-icon>
            </v-list-item-icon>
          </v-list-item>

          <v-divider v-if="participantIndex !== sortedParticipants.length - 1" />
        </template>
      </v-list>
    </v-card-text>
  </v-card>
</template>

<script>
import { computed } from '@vue/composition-api';

import store from '@/store';
import { format } from 'date-fns';

import { getRepriseTabSessionColor } from '@/helpers/utils';

export default {
  name: 'SessionCard',
  props: {
    sessionObject: { type: Object, default: () => {} },
    isInstructorsLoading: { type: Boolean, default: true }
  },
  emits: ['edit-session'],
  setup (props) {
    const session = computed(() => props.sessionObject);

    const participantName = participant => `${participant?.firstname || ''} ${participant?.lastname || ''}`.trim();

    const sessionInstructors = computed(() => {
      return (session.value?.instructors || []).reduce((acc, instructor) => {
        if(!!instructor?.wtitle) {
          acc.push({name: instructor.wtitle, horse: instructor.horse});
          return acc;
        }

        const instructorDetail = store.getters.instructor(instructor.uuid);
        if(instructorDetail?.fullname) {
          acc.push({name: instructorDetail.fullname, horse: instructor.horse});
        }

        return acc;
      }, []);
    });

    const sessionTimeLabel = computed(() => (`${format(new Date(session.value?.start_time * 1000), "HH'h'mm")} - ${format(new Date(session.value?.end_time * 1000), "HH'h'mm")}`));

    const sessionHorsesCount = computed(() => (session.value?.horses?.length || 0));

    const participantAssignedHorsesCount = computed(() => (session.value?.participants?.filter(participant => !!participant.horse)?.length || 0));

    const sessionAssignedHorsesCount = computed(() => ((session.value?.horses || []).filter(h => (session.value?.participants || []).every(p => !p.horse || p.horse !== h.uuid) && (session.value?.instructors || []).every(i => !i.horse || i.horse !== h.uuid))?.length || 0));

    const instructorAssignedHorsesCount = computed(() => ((session.value?.instructors || []).filter(i => !!i.horse)?.length || 0));

    const sortedParticipants = computed(() => (session.value?.participants || []).sort((a, b) => {
      // 1. Assigned participants first
      if(a?.horse && !b?.horse) return -1;
      if(!a?.horse && b?.horse) return 1;

      // 2. Alphabetical fallback
      const fullnameA = `${a?.firstname} ${a?.lastname}`;
      const fullnameB = `${b?.firstname} ${b?.lastname}`;
      return fullnameA.localeCompare(fullnameB, undefined, { sensitivity: 'base' });
    }));

    const sessionColor = computed(() => getRepriseTabSessionColor(session.value));
    const showSingleInstructor = computed(() => sessionInstructors.value.length === 1);
    const showMultipleInstructors = computed(() => sessionInstructors.value.length > 1);

    return {
      session,
      sessionInstructors,
      participantName,
      sessionTimeLabel,
      sessionHorsesCount,
      participantAssignedHorsesCount,
      sessionAssignedHorsesCount,
      sortedParticipants,
      sessionColor,
      instructorAssignedHorsesCount,
      showSingleInstructor,
      showMultipleInstructors
    };
  }
};
</script>

<style>
.session-card {
  .v-btn__content {
    overflow: hidden;
    flex: 1;
  }
  .v-chip.no-hover:hover::before {
    opacity: 0;
  }
}
</style>
